import styled from 'styled-components';
import { PHONE_MEDIA_QUERY } from '../../common/responsive';

// Define the color variant types
type ColorVariant =
  | 'text-primary'
  | 'text-secondary'
  | 'text-regular'
  | 'text-disabled'
  | 'text-title'
  | 'text-error'
  | 'text-error-light'
  | 'primary'
  | 'primary-dark'
  | 'primary-light'
  | 'primary-lighter'
  | 'primary-lightest'
  | 'primary-error'
  | 'primary-error-light'
  | 'toast-error'
  | 'toast-success';

// Define the props interface
interface TextProps {
  $color?: string;
  $variant?: ColorVariant;
}

// Helper function to get color from theme
const getColorFromTheme = (variant, theme) => {
  if (!variant) return theme.text.regular;

  const colorMap: Record<ColorVariant, string> = {
    // Text colors
    'text-primary': theme.text.primary,
    'text-secondary': theme.text.secondary,
    'text-regular': theme.text.regular,
    'text-disabled': theme.text.disabled,
    'text-title': theme.text.title,
    'text-error': theme.text.error,
    'text-error-light': theme.text.errorLight,

    // Primary colors
    primary: theme.primary.primary,
    'primary-dark': theme.primary.dark,
    'primary-light': theme.primary.light,
    'primary-lighter': theme.primary.lighter,
    'primary-lightest': theme.primary.background,
    'primary-error': theme.status.error.primary,
    'primary-error-light': theme.status.error.background,

    // Toast colors
    'toast-error': theme.error,
    'toast-success': theme.success,
  };

  return colorMap[variant] || 'inherit';
};

export const ButtonMediumSemiText = styled.p<TextProps>`
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 18px;
  letter-spacing: normal;
  color: ${({ $color, $variant, theme }) =>
    $color || getColorFromTheme($variant, theme)};
`;

export const Heading1Text = styled.p<TextProps>`
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 56px;
  line-height: 67px;
  letter-spacing: -2%;
  color: ${({ $color, $variant, theme }) =>
    $color || getColorFromTheme($variant, theme)};
`;

export const Heading2Text = styled.p<TextProps>`
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 48px;
  line-height: 50px;
  letter-spacing: -2%;
  color: ${({ $color, $variant, theme }) =>
    $color || getColorFromTheme($variant, theme)};
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 24px;
  }
`;

export const Heading3Text = styled.p<TextProps>`
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 34px;
  line-height: 40px;
  letter-spacing: -2%;
  color: ${({ $color, $variant, theme }) =>
    $color || getColorFromTheme($variant, theme)};
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 24px;
  }
`;

export const Heading4Text = styled.p<TextProps>`
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 28px;
  line-height: 36px;
  letter-spacing: -2%;
  color: ${({ $color, $variant, theme }) =>
    $color || getColorFromTheme($variant, theme)};
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 18px;
  }
`;

export const Heading5Text = styled.p<TextProps>`
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 22px;
  line-height: 28px;
  letter-spacing: 0%;
  color: ${({ $color, $variant, theme }) =>
    $color || getColorFromTheme($variant, theme)};
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 16px;
  }
`;

export const ParagraphLargeText = styled.p<TextProps>`
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 19px;
  line-height: 24px;
  letter-spacing: auto;
  color: ${({ $color, $variant, theme }) =>
    $color || getColorFromTheme($variant, theme)};
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 15px;
  }
`;

export const ParagraphMediumText = styled.p<TextProps>`
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 16px;
  letter-spacing: auto;
  color: ${({ $color, $variant, theme }) =>
    $color || getColorFromTheme($variant, theme)};
`;

export const ParagraphSmallText = styled.p<TextProps>`
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: auto;
  color: ${({ $color, $variant, theme }) =>
    $color || getColorFromTheme($variant, theme)};
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 12px;
  }
`;

export const TitleLarge = styled.h2<TextProps>`
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 26px;
  line-height: 32px;
  letter-spacing: auto;
  color: ${({ $color, $variant, theme }) =>
    $color || getColorFromTheme($variant, theme)};
`;

export const TitleMedium = styled.h2<TextProps>`
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 18px;
  line-height: 24px;
  letter-spacing: auto;
  color: ${({ $color, $variant, theme }) =>
    $color || getColorFromTheme($variant, theme)};
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 16px;
  }
`;

export const TitleSmall = styled.h3<TextProps>`
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
  letter-spacing: auto;
  color: ${({ $color, $variant, theme }) =>
    $color || getColorFromTheme($variant, theme)};
`;

export const BodyLarge = styled.p<TextProps>`
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: auto;
  color: ${({ $color, $variant, theme }) =>
    $color || getColorFromTheme($variant, theme)};
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 14px;
  }
`;

export const BodySmall = styled.p<TextProps>`
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 13px;
  line-height: 20px;
  letter-spacing: auto;
  color: ${({ $color, $variant, theme }) =>
    $color || getColorFromTheme($variant, theme)};
`;

export const BodyMedium = styled.p<TextProps>`
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 13px;
  line-height: 22px;
  letter-spacing: auto;
  color: ${({ $color, $variant, theme }) =>
    $color || getColorFromTheme($variant, theme)};
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 12px;
  }
`;

export const CaptionSmall = styled.p<TextProps>`
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 11px;
  line-height: 12px;
  letter-spacing: auto;
  color: ${({ $color, $variant, theme }) =>
    $color || getColorFromTheme($variant, theme)};
`;
